import type { GenerationResult } from "../GenerationResult";
import { getTask } from "../api/getTask";

type ResultEntry = {
    status: "loading" | "loaded" | "error";
    result: GenerationResult | null;
};

const cache = new Map<string, ResultEntry>();
const listeners = new Set<() => void>();
const inflight = new Set<string>();

function emit() {
    listeners.forEach((l) => l());
}

function cacheKey(taskId: string, projectId: string): string {
    return `${projectId}::${taskId}`;
}

export function getGenerationResult(
    taskId: string | null,
    projectId: string | null
): ResultEntry | null {
    if (!taskId || !projectId) return null;
    const key = cacheKey(taskId, projectId);
    const existing = cache.get(key);
    if (existing) return existing;

    if (!inflight.has(key)) {
        inflight.add(key);
        cache.set(key, { status: "loading", result: null });

        getTask(projectId, taskId).then(
            (task) => {
                cache.set(key, { status: "loaded", result: task.result ?? null });
                inflight.delete(key);
                emit();
            },
            () => {
                cache.set(key, { status: "error", result: null });
                inflight.delete(key);
                emit();
            }
        );
    }

    return cache.get(key) ?? null;
}

export function subscribeGenerationResult(cb: () => void): () => void {
    listeners.add(cb);
    return () => {
        listeners.delete(cb);
    };
}

/**
 * 取得した結果を捨てて、次の照会で取り直させる。
 *
 * **これが無かったため、一度開くと以後ずっと古いままだった**（記録 22）。生成が完了しても
 * 表示は変わらず、ペインを閉じて開き直しても同じで、アドインを閉じて開き直すまで直らない。
 * 通信が一度失敗した場合も同じで、以後ずっとエラー表示になる。Web コンソールは通知の購読で
 * 取り直すが、アドインには通知の仕組みが無いので、開くたびに捨てる口が要る。
 *
 * 引数を省くと全件捨てる。
 */
export function invalidateGenerationResult(
    taskId?: string | null,
    projectId?: string | null
): void {
    if (!taskId || !projectId) {
        cache.clear();
        emit();
        return;
    }
    cache.delete(cacheKey(taskId, projectId));
    emit();
}
